// @flow
import { sendSuccessMessage } from "@modules/app/actionCreator";
import { all, put, takeEvery, call } from "redux-saga/effects";
import type { Action } from "redux-action";
import config from "@config";
import http from "@http";
import {
  GetAgreements,
  PutAgreements,
  GetSlaSchema,
  GetSlaPerformanceReportDetails,
  ExportSlaPerformanceReport,
} from "@modules/client/constants/actions";
import { getSlaPerformanceSelectedAndExcluded } from "@modules/client/selectors/index.js";
import { getFormValues } from "redux-form";
import { select } from "redux-saga/effects";
import actionCreators from "@modules/client/actionCreator";
import type { SlaAgreement, SlaSchema } from "@modules/client/reducer/sla/types";
import { downloadCsvFile } from "@sagas/utils";
import { parseSchema } from "@utils/schema";

type ItemResponse<T> = $Exact<T>;

type ClientRequest = $Exact<{
  clientId: string,
}>;

type SlaUpdate = $Exact<{
  ...ClientRequest,
  formData: SlaAgreement,
  slaKeyId: string,
}>;

export function* onGetAgreements({ payload }: Action<ClientRequest>): Generator<*, void, *> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const response = yield call(http.get, `/api/v1/client-admin/client/${payload.clientId}/sla`);
    yield put(actionCreators.GetAgreementsSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.GetAgreementsError(e.response));
  }
}

export function* onPutAgreements({
  payload,
}: Action<SlaUpdate>): Generator<*, void, ItemResponse<SlaAgreement>> {
  try {
    if (!payload || !payload.clientId || !payload.slaKeyId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    yield call(
      http.put,
      `/api/v1/client-admin/client/${payload.clientId}/sla/${payload.slaKeyId}`,
      payload.formData,
    );
    yield all([
      put(actionCreators.PutAgreementsSuccess()),
      put(sendSuccessMessage("SLA_MEASURES_UPDATED")),
      put(actionCreators.ToggleSlaClientModal(false)),
      put(actionCreators.GetAgreements({ clientId: payload.clientId })),
    ]);
  } catch (e) {
    yield put(actionCreators.PutAgreementsError(e.response));
  }
}

export function* onGetSlaSchema({
  payload,
}: Action<SlaUpdate>): Generator<*, void, ItemResponse<SlaSchema>> {
  try {
    if (!payload || !payload.clientId) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const data = yield parseSchema("ClientSlaDTO", { clientId: payload.clientId });
    yield put(actionCreators.GetSlaSchemaSuccess(data));
  } catch (e) {
    yield put(actionCreators.GetSlaSchemaError(e.response));
  }
}

export function* onGetSlaPerformanceReportDetails({
  payload,
}: Action<number>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }
    const slaPerformanceFilters = yield select(getFormValues("SlaPerformanceFilters"));
    const selectedExcluded = yield select(getSlaPerformanceSelectedAndExcluded);
    const params = {
      selected_organisational_unit_ids: selectedExcluded.selected,
      excluded_organisational_unit_ids: selectedExcluded.excluded,
      from_date: slaPerformanceFilters.from_date,
      to_date: slaPerformanceFilters.to_date,
      period_type: slaPerformanceFilters.period_type,
    };

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/client-admin/client/${payload}/sla/performance`,
      params,
    );
    yield put(actionCreators.GetSlaPerformanceReportDetailsSuccess(response));
  } catch (e) {
    yield put(actionCreators.GetSlaPerformanceReportDetailsError(e.response));
  }
}

export function* onExportSlaPerformanceReport({ payload }: Action<number>): Generator<*, void, *> {
  try {
    if (!payload) {
      throw Error(`Invalid payload provided:\n\n${JSON.stringify(payload, null, 2)}\n\n`);
    }

    const slaPerformanceFilters = yield select(getFormValues("SlaPerformanceFilters"));
    const selectedExcluded = yield select(getSlaPerformanceSelectedAndExcluded);
    const params = {
      selected_organisational_unit_ids: selectedExcluded.selected,
      excluded_organisational_unit_ids: selectedExcluded.excluded,
      from_date: slaPerformanceFilters.from_date,
      to_date: slaPerformanceFilters.to_date,
      period_type: slaPerformanceFilters.period_type,
    };

    const response = yield call(
      http.post,
      `${config.endpointBaseUrl}api/v1/client-admin/client/${payload}/sla/performance/export`,
      params,
    );
    downloadCsvFile(response.data, "SLA Performance report.csv");
    yield put(actionCreators.ExportSlaPerformanceReportSuccess(response.data));
  } catch (e) {
    yield put(actionCreators.ExportSlaPerformanceReportError(e.response));
  }
}

export function* ClientSlaSagas(): Generator<*, void, void> {
  yield all([
    takeEvery(GetAgreements, onGetAgreements),
    takeEvery(PutAgreements, onPutAgreements),
    takeEvery(GetSlaSchema, onGetSlaSchema),
    takeEvery(GetSlaPerformanceReportDetails, onGetSlaPerformanceReportDetails),
    takeEvery(ExportSlaPerformanceReport, onExportSlaPerformanceReport),
  ]);
}

export default ClientSlaSagas;
